import React, { useEffect, useState, useCallback } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trophy, Medal, CheckCircle2, Coins, Crown, Video } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const nf = (n) => (Number(n) || 0).toLocaleString("id-ID");
const rankCls = (i) => (i === 0 ? "text-yellow-500" : i === 1 ? "text-gray-400" : i === 2 ? "text-amber-700" : "text-muted-foreground");

const PointTable = ({ rows }) => (
  <div className="rounded-2xl border border-border bg-white overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-showty-navy text-white"><tr className="text-left">
          <th className="px-3 py-2 text-xs font-medium">#</th>
          <th className="px-3 py-2 text-xs font-medium">Nama</th>
          <th className="px-3 py-2 text-xs font-medium">Point Valid</th>
        </tr></thead>
        <tbody>
          {(!rows || rows.length === 0) && <tr><td colSpan={3} className="px-4 py-8 text-center text-muted-foreground">Belum ada data point</td></tr>}
          {rows?.map((e, i) => (
            <tr key={e.user_id} className="border-t border-border even:bg-showty-cream/40" data-testid={`pt-lb-row-${i}`}>
              <td className="px-3 py-2">{i < 3 ? <Medal className={`w-4 h-4 ${rankCls(i)}`} /> : <span className="text-muted-foreground">{i + 1}</span>}</td>
              <td className="px-3 py-2 font-medium text-showty-navy">{e.name}</td>
              <td className="px-3 py-2 font-semibold text-showty-red">{nf(e.points)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const PointsLeaderboards = () => {
  const [period, setPeriod] = useState("");
  const [kol, setKol] = useState([]);
  const [aff, setAff] = useState([]);
  const load = useCallback(async () => {
    const p = period ? `&period=${period}` : "";
    const [k, a] = await Promise.all([
      api.get(`/points/leaderboard?path=kol${p}`), api.get(`/points/leaderboard?path=aff${p}`),
    ]);
    setKol(k.data); setAff(a.data);
  }, [period]);
  useEffect(() => { load().catch(() => {}); }, [load]);
  return (
    <div data-testid="points-leaderboards">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display text-2xl text-showty-navy flex items-center gap-2"><Coins className="w-5 h-5 text-showty-red" /> Papan Juara Point</h3>
          <p className="text-sm text-muted-foreground">Peringkat otomatis dari point valid (setelah koreksi/refund), bukan saldo setelah redeem. KOL & Affiliator terpisah.</p>
        </div>
        <div><Label className="text-xs">Periode (YYYY-MM)</Label><Input className="mt-1 h-9 w-40" placeholder="kosong = semua" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="pt-lb-period" /></div>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div><p className="text-sm font-medium text-showty-navy mb-2 flex items-center gap-1.5"><Crown className="w-4 h-4 text-showty-red" /> Beauty Creator (KOL)</p><PointTable rows={kol} /></div>
        <div><p className="text-sm font-medium text-showty-navy mb-2 flex items-center gap-1.5"><Video className="w-4 h-4 text-showty-navy" /> Affiliator</p><PointTable rows={aff} /></div>
      </div>
    </div>
  );
};

const EntryRows = ({ entries }) => (
  <div className="rounded-2xl border border-border bg-white overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-showty-navy text-white">
          <tr className="text-left">
            <th className="px-3 py-2 text-xs font-medium">#</th>
            <th className="px-3 py-2 text-xs font-medium">Nama KOL</th>
            <th className="px-3 py-2 text-xs font-medium">Tier</th>
            <th className="px-3 py-2 text-xs font-medium">Followers</th>
            <th className="px-3 py-2 text-xs font-medium">Reach</th>
            <th className="px-3 py-2 text-xs font-medium">ER</th>
          </tr>
        </thead>
        <tbody>
          {(!entries || entries.length === 0) && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">Belum ada data</td></tr>}
          {entries?.map((e, i) => (
            <tr key={i} className="border-t border-border even:bg-showty-cream/40" data-testid={`lb-row-${i}`}>
              <td className="px-3 py-2">{i < 3 ? <Medal className={`w-4 h-4 ${rankCls(i)}`} /> : <span className="text-muted-foreground">{i + 1}</span>}</td>
              <td className="px-3 py-2 font-medium text-showty-navy">{e.name}</td>
              <td className="px-3 py-2"><span className="text-[11px] rounded-full bg-showty-pink/40 px-2 py-0.5 text-showty-navy">{e.tier || "—"}</span></td>
              <td className="px-3 py-2">{nf(e.followers)}</td>
              <td className="px-3 py-2">{nf(e.reach)}</td>
              <td className="px-3 py-2 font-semibold text-showty-red">{e.er}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export default function LeaderboardPanel({ editable = false }) {
  const [cands, setCands] = useState([]);
  const [pub, setPub] = useState(null);
  const [period, setPeriod] = useState("");
  const [topN, setTopN] = useState("10");

  const loadPub = useCallback(() => api.get("/leaderboard/published").then((r) => setPub(r.data)).catch(() => {}), []);
  const loadCands = useCallback(() => { if (editable) api.get("/leaderboard/candidates").then((r) => setCands(r.data)).catch(() => {}); }, [editable]);
  useEffect(() => { loadPub(); loadCands(); }, [loadPub, loadCands]);

  const publish = async () => {
    try { await api.post("/leaderboard/publish", { period, top_n: Number(topN) }); toast.success("Papan Juara dipublikasikan ke member"); loadPub(); }
    catch (e) { toast.error("Gagal publikasikan"); }
  };

  if (!editable) {
    return (
      <div data-testid="leaderboard-panel">
        <PointsLeaderboards />
        <div className="mt-8">
        <h3 className="font-display text-2xl text-showty-navy flex items-center gap-2 mb-1"><Trophy className="w-5 h-5 text-showty-gold" /> Papan Juara ER</h3>
        <p className="text-sm text-muted-foreground mb-4">{pub?.period ? `Periode ${pub.period}` : "Peringkat KOL berdasarkan Engagement Rate tertinggi."}</p>
        {pub?.entries?.length ? <EntryRows entries={pub.entries} /> : (
          <div className="rounded-2xl border border-dashed border-border bg-white/50 py-16 text-center text-sm text-muted-foreground" data-testid="leaderboard-empty">
            <Trophy className="w-6 h-6 mx-auto mb-2 text-showty-gold/60" /> Papan juara belum dipublikasikan
          </div>
        )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="leaderboard-panel">
      <PointsLeaderboards />
      <div className="mt-8 flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display text-2xl text-showty-navy flex items-center gap-2"><Trophy className="w-5 h-5 text-showty-gold" /> Papan Juara (Auto ER)</h3>
          <p className="text-sm text-muted-foreground">Peringkat otomatis dari data Report (ER tertinggi). Approve & publish agar tampil ke member.</p>
        </div>
        <div className="flex items-end gap-2">
          <div><Label className="text-xs">Periode</Label><Input className="mt-1 h-9 w-32" placeholder="Juni 2026" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="lb-period" /></div>
          <div><Label className="text-xs">Top</Label>
            <Select value={topN} onValueChange={setTopN}>
              <SelectTrigger className="mt-1 h-9 w-20" data-testid="lb-topn"><SelectValue /></SelectTrigger>
              <SelectContent>{["5", "10", "15", "20"].map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={publish} className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white h-9" data-testid="lb-publish"><CheckCircle2 className="w-4 h-4 mr-1" /> Approve & Publish</Button>
        </div>
      </div>

      {pub && <p className="text-xs text-green-700 mb-3" data-testid="lb-published-info">✓ Terpublikasi{pub.period ? ` — Periode ${pub.period}` : ""} ({pub.entries?.length || 0} KOL) oleh {pub.approved_by || "Manager"}</p>}

      <p className="text-sm font-medium text-showty-navy mb-2">Kandidat peringkat (Top 20 ER):</p>
      <EntryRows entries={cands} />
    </div>
  );
}
